import moment from 'moment';

import schema from './validate';
import api, { optimiseApi } from '../axiosApi';
import { stringifyQuery, DATE_FORMAT } from '../../utils/query';

export const getRoutes = async (depotId, sectionId, date) => {
  const { data } = await api.get(
    `/depot/${depotId}/section/${sectionId || ''}/route?${stringifyQuery({
      date: moment.utc(date, DATE_FORMAT).toDate().getTime(),
    })}`
  );

  try {
    const result = await schema.validateAsync(data);

    return {
      routes: result.routes.sort((a, b) =>
        a.routeCode.localeCompare(b.routeCode)
      ),
      depotStats: result.depotStats,
    };
  } catch (error) {
    // eslint-disable-next-line
    console.warn('Invalid getRoutes data', error);
  }
};

export const getRoutesTransfer = async (depotId, date) => {
  const { data } = await api.get(
    `/depot/${depotId}/route?${stringifyQuery({
      date: moment.utc(date, DATE_FORMAT).toDate().getTime(),
    })}`
  );
  return data;
};

export const getRouteDetail = async (
  depotCode,
  routeId,
  routePlannerDowngrades
) => {
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}?route-planner-downgrades=${routePlannerDowngrades}`
  );

  return data;
};

export const resyncRouteDetail = async (
  depotCode,
  routeId,
  routePlannerDowngrades
) => {
  const { data } = await api.put(
    `/depot/${depotCode}/route/${routeId}/resync?route-planner-downgrades=${routePlannerDowngrades}`
  );

  return data;
};

export const unlockRoute = async (
  depotCode,
  userName,
  { routeId, checksum },
  forceUnlock
) =>
  api.post(
    `/depot/${depotCode}/route/unlock`,
    {
      userName,
      route: [{ routeId, checksum, forceUnlock }],
    },
    {
      ignoreSnackbarError: true,
    }
  );

export const setCutOffTime = async (
  depotCode,
  date,
  { waveCutOff, departTime }
) =>
  api.put(
    `/depot/${depotCode}/routes`,
    {
      waveCutOff,
      departTime,
      date: `${moment.utc(date, DATE_FORMAT).toDate().getTime()}`,
    },
    {
      ignoreSnackbarError: true,
    }
  );

export const setDisasterRecoveryMode = async (depotCode) =>
  api.put(`/depot/${depotCode}/mode/disaster-recovery`, null, {
    ignoreSnackbarError: true,
  });

export const lockRoute = async (
  depotCode,
  userName,
  { routeId, checksum },
  stops = []
) =>
  api.post(
    `/depot/${depotCode}/route/lock`,
    {
      userName,
      stops,
      route: [{ routeId, checksum }],
    },
    {
      ignoreSnackbarError: true,
    }
  );

export const changeRouteStatus = async (depotCode, routeStatus, routeId) =>
  api.post(
    `/depot/${depotCode}/route/${routeId}/status`,
    {
      routeStatus,
    },
    {
      ignoreSnackbarError: true,
    }
  );

export const scheduleRoute = async (
  depotCode,
  routeId,
  scheduleData,
  abortController
) =>
  optimiseApi.post(
    `/depot/${depotCode}/route/${routeId}/schedule`,
    scheduleData,
    { ignoreSnackbarError: true, signal: abortController?.signal }
  );

export const saveRoute = async (depotCode, routeId, routeData) =>
  optimiseApi.put(`/depot/${depotCode}/route/${routeId}`, routeData, {
    ignoreSnackbarError: true,
  });

export const createRoute = async (depotCode, route) =>
  optimiseApi.post(`/depot/${depotCode}/route/`, route, {
    ignoreSnackbarError: true,
  });

export const validateRouteCode = async (depotCode, routeCode, date) => {
  const { data = {} } = await optimiseApi.get(
    `/depot/${depotCode}/route/${routeCode}/validate?${stringifyQuery({
      date: moment.utc(date, DATE_FORMAT).format('YYYY-MM-DD'),
    })}`
  );

  return data;
};

export const getWaveChoiceRouteId = async (depotCode, routeId) => {
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}/choice-route`
  );
  return data;
};

export const getPartiallyCaddedStops = async (depotCode, routeId) => {
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}/partially-cadded-stops`
  );
  return data;
};

export const getDriverLocationForDepartedRoute = async ([
  depotCode,
  routeId,
  limit,
]) => {
  const limitQuery = limit ? `?limit=${limit}` : '';
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}/driver-location${limitQuery}`,
    null,
    {
      ignoreSnackbarError: true,
    }
  );
  return data;
};

export const closeTote = async (depotCode, routeCode) =>
  api.put(`/depot/${depotCode}/route/${routeCode}/close-tote`);
